import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Package, Search, Clock, Download, Trash2, AlertTriangle, Loader2, Eye, X, User, ShoppingCart, Receipt } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const AdminOrders = () => {
  const { api } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [total, setTotal] = useState(0);
  const [clearDialog, setClearDialog] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      const response = await api.get('/admin/orders?limit=100');
      setOrders(response.data.orders || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const handleClearOrders = async () => {
    setClearing(true);
    try {
      await api.delete('/admin/orders/clear');
      toast.success('All orders cleared successfully');
      setClearDialog(false);
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear orders');
    } finally {
      setClearing(false);
    }
  };

  const formatPrice = (price) => `₹${price?.toFixed(2) || '0.00'}`;
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const filteredOrders = orders.filter(o => 
    !searchQuery || 
    o.invoice_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    o.id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    o.user_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    o.cart_code?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalRevenue = orders.reduce((sum, o) => sum + (o.final_total || o.total || 0), 0);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold font-['Outfit'] text-slate-800">Orders</h1>
          <p className="text-slate-500">View and manage all orders ({total} total)</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-slate-200 text-slate-600 hover:bg-slate-50">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button 
            variant="outline" 
            className="border-red-200 text-red-600 hover:bg-red-50"
            onClick={() => setClearDialog(true)}
            disabled={orders.length === 0}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Clear All
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Orders</p>
                <p className="text-2xl font-bold text-slate-800">{total}</p>
              </div>
              <Package className="w-8 h-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Revenue</p>
                <p className="text-2xl font-bold text-emerald-600">{formatPrice(totalRevenue)}</p>
              </div>
              <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                <span className="text-emerald-600 text-lg">₹</span>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Avg. Order Value</p>
                <p className="text-2xl font-bold text-teal-600">
                  {orders.length > 0 ? formatPrice(totalRevenue / orders.length) : '₹0.00'}
                </p>
              </div>
              <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                <span className="text-teal-600 text-lg">~</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card className="mb-6 border-slate-200">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              data-testid="order-search"
              placeholder="Search by invoice, user email, or cart code..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 border-slate-200"
            />
          </div>
        </CardContent>
      </Card>

      {/* Orders Table */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="text-center py-16">
          <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No orders found</p>
        </div>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-200">
                  <TableHead className="text-slate-500">Invoice</TableHead>
                  <TableHead className="text-slate-500">Date</TableHead>
                  <TableHead className="text-slate-500">Customer</TableHead>
                  <TableHead className="text-slate-500">Cart</TableHead>
                  <TableHead className="text-slate-500">Items</TableHead>
                  <TableHead className="text-slate-500">Total</TableHead>
                  <TableHead className="text-slate-500">Status</TableHead>
                  <TableHead className="text-slate-500 w-20">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrders.map((order) => (
                  <TableRow key={order.id} className="border-slate-100 hover:bg-slate-50">
                    <TableCell className="font-mono text-sm text-slate-700">{order.invoice_number}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm text-slate-600">
                        <Clock className="w-3 h-3 text-slate-400" />
                        {formatDate(order.created_at)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm text-slate-600">
                        <User className="w-3 h-3 text-slate-400" />
                        <span className="truncate max-w-[150px]">{order.user_email || 'N/A'}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="border-teal-300 text-teal-700">
                        {order.cart_code || 'N/A'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {order.items?.slice(0, 2).map((item, i) => (
                          item.image_url && (
                            <img
                              key={i}
                              src={item.image_url}
                              alt={item.name}
                              className="w-8 h-8 rounded object-cover border border-slate-200"
                            />
                          )
                        ))}
                        <span className="text-xs text-slate-500">
                          {order.item_count || order.items?.length || 0} items
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="font-semibold text-slate-800">{formatPrice(order.final_total || order.total)}</TableCell>
                    <TableCell>
                      <Badge className={
                        order.payment_status === 'completed' 
                          ? 'bg-emerald-100 text-emerald-800' 
                          : 'bg-amber-100 text-amber-800'
                      }>
                        {order.payment_status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8 text-slate-500 hover:text-teal-600 hover:bg-teal-50"
                        onClick={() => setSelectedOrder(order)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Clear Orders Dialog */}
      <Dialog open={clearDialog} onOpenChange={setClearDialog}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Clear All Orders
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              Are you sure you want to clear all {total} orders? This action cannot be undone and will permanently delete all order history.
            </DialogDescription>
          </DialogHeader>

          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
            <p className="font-medium">Warning:</p>
            <ul className="list-disc list-inside mt-1 space-y-1 text-red-700">
              <li>All order records will be permanently deleted</li>
              <li>Revenue statistics will be reset to zero</li>
              <li>This action cannot be reversed</li>
            </ul>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setClearDialog(false)} className="border-slate-200">
              Cancel
            </Button>
            <Button 
              onClick={handleClearOrders} 
              disabled={clearing} 
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {clearing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Clearing...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear All Orders
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order Detail Dialog */}
      <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent className="max-w-lg bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-800">
              <Receipt className="w-5 h-5 text-teal-600" /> Invoice Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-4">
              {/* Invoice Header */}
              <div className="p-4 bg-slate-50 rounded-xl space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-sm text-slate-600">{selectedOrder.invoice_number}</span>
                  <Badge className={selectedOrder.payment_status === 'completed' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}>
                    {selectedOrder.payment_status}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs text-slate-500">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {formatDate(selectedOrder.created_at)}
                  </span>
                  <span className="flex items-center gap-1">
                    <ShoppingCart className="w-3 h-3" /> Cart: {selectedOrder.cart_code || 'N/A'}
                  </span>
                  <span className="flex items-center gap-1 col-span-2">
                    <User className="w-3 h-3" /> {selectedOrder.user_email || 'N/A'}
                  </span>
                </div>
              </div>

              {/* Items */}
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-2">Items ({selectedOrder.items?.length || 0})</h4>
                <ScrollArea className="h-[200px]">
                  <div className="space-y-2">
                    {selectedOrder.items?.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
                        <div className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center overflow-hidden">
                          {item.image_url ? (
                            <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                          ) : (
                            <Package className="w-5 h-5 text-slate-300" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-slate-800 truncate">{item.name}</p>
                          <p className="text-xs text-slate-500">₹{item.price} × {item.quantity}</p>
                        </div>
                        <p className="font-semibold text-sm text-slate-700">₹{(item.price * item.quantity).toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>

              {/* Applied Offers */}
              {selectedOrder.applied_offers?.length > 0 && (
                <div className="p-3 bg-teal-50 rounded-lg">
                  <p className="text-xs font-medium text-teal-700 mb-1">Applied Offers</p>
                  {selectedOrder.applied_offers.map((offer, idx) => (
                    <p key={idx} className="text-xs text-teal-600">{offer.title} - Saved ₹{offer.discount?.toFixed(2)}</p>
                  ))}
                </div>
              )}

              {/* Totals */}
              <div className="border-t border-slate-200 pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Subtotal</span>
                  <span className="text-slate-700">₹{selectedOrder.subtotal?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Tax</span>
                  <span className="text-slate-700">₹{selectedOrder.tax?.toFixed(2)}</span>
                </div>
                {selectedOrder.discount > 0 && (
                  <div className="flex justify-between text-sm text-teal-600">
                    <span>Discount</span>
                    <span>-₹{selectedOrder.discount?.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg pt-2 border-t border-slate-200">
                  <span className="text-slate-800">Total</span>
                  <span className="text-teal-600">₹{(selectedOrder.final_total || selectedOrder.total)?.toFixed(2)}</span>
                </div>
              </div>

              {/* Payment Info */}
              <div className="text-xs text-slate-400">
                <p>Payment Method: {selectedOrder.payment_method || 'Card'}</p>
                {selectedOrder.payment_id && <p>Payment ID: {selectedOrder.payment_id}</p>}
                <p>User ID: {selectedOrder.user_id}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminOrders;
